import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  WidthType,
} from 'docx'
import type { MeetingMinutesData } from '@ai-meeting/shared'
import { formatDate, formatTime } from './formatDateTime'

export type MeetingMinutesLocale = 'ja' | 'vi' | 'en'

interface BuildOptions {
  title: string
  createdAt?: number
  durationSeconds?: number
  participants?: string[]
  locale: MeetingMinutesLocale
}

interface MinutesStrings {
  documentTitle: string
  section1: string
  itemColumn: string
  contentColumn: string
  meetingName: string
  dateTime: string
  participants: string
  section2: string
  section3: string
  topic: (index: number) => string
  overview: string
  mainDiscussion: string
  conclusion: string
  section4: string
  no: string
  taskContent: string
  assignee: string
  deadline: string
  empty: string
}

const STRINGS: Record<MeetingMinutesLocale, MinutesStrings> = {
  ja: {
    documentTitle: '議事録',
    section1: '1. 会議情報',
    itemColumn: '項目',
    contentColumn: '内容',
    meetingName: '会議名',
    dateTime: '日時',
    participants: '参加者',
    section2: '2. 会議の目的',
    section3: '3. 議論内容',
    topic: (i) => `議題${i}`,
    overview: '概要',
    mainDiscussion: '主な議論',
    conclusion: '結論',
    section4: '4. アクションアイテム',
    no: 'No',
    taskContent: '対応内容',
    assignee: '担当',
    deadline: '期限',
    empty: '—',
  },
  vi: {
    documentTitle: 'Biên bản họp',
    section1: '1. Thông tin cuộc họp',
    itemColumn: 'Mục',
    contentColumn: 'Nội dung',
    meetingName: 'Tên cuộc họp',
    dateTime: 'Ngày giờ',
    participants: 'Người tham gia',
    section2: '2. Mục đích cuộc họp',
    section3: '3. Nội dung thảo luận',
    topic: (i) => `Chủ đề ${i}`,
    overview: 'Tổng quan',
    mainDiscussion: 'Nội dung thảo luận chính',
    conclusion: 'Kết luận',
    section4: '4. Action Items',
    no: 'No',
    taskContent: 'Nội dung công việc',
    assignee: 'Người phụ trách',
    deadline: 'Deadline',
    empty: '—',
  },
  en: {
    documentTitle: 'Meeting Minutes',
    section1: '1. Meeting Information',
    itemColumn: 'Item',
    contentColumn: 'Content',
    meetingName: 'Meeting Name',
    dateTime: 'Date & Time',
    participants: 'Participants',
    section2: '2. Meeting Purpose',
    section3: '3. Discussion Content',
    topic: (i) => `Topic ${i}`,
    overview: 'Overview',
    mainDiscussion: 'Main Discussion',
    conclusion: 'Conclusion',
    section4: '4. Action Items',
    no: 'No',
    taskContent: 'Task Content',
    assignee: 'Assignee',
    deadline: 'Deadline',
    empty: '—',
  },
}

const HEADER_SHADING = { type: ShadingType.CLEAR, color: 'auto', fill: 'E7E6E6' }
const THIN_BORDER = { style: BorderStyle.SINGLE, size: 4, color: '808080' }
const TABLE_BORDERS = {
  top: THIN_BORDER,
  bottom: THIN_BORDER,
  left: THIN_BORDER,
  right: THIN_BORDER,
  insideHorizontal: THIN_BORDER,
  insideVertical: THIN_BORDER,
}

function sectionHeading(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 320, after: 160 },
    children: [new TextRun({ text, bold: true, size: 26 })],
  })
}

function subHeading(text: string): Paragraph {
  return new Paragraph({
    spacing: { before: 160, after: 80 },
    children: [new TextRun({ text, bold: true })],
  })
}

function bullet(text: string): Paragraph {
  return new Paragraph({
    bullet: { level: 0 },
    spacing: { after: 60 },
    children: [new TextRun(text)],
  })
}

function plain(text: string): Paragraph {
  return new Paragraph({ children: [new TextRun(text)] })
}

function cell(
  text: string,
  opts: { bold?: boolean; header?: boolean; width?: number } = {}
): TableCell {
  return new TableCell({
    width: opts.width ? { size: opts.width, type: WidthType.PERCENTAGE } : undefined,
    shading: opts.header ? HEADER_SHADING : undefined,
    children: [
      new Paragraph({
        children: [new TextRun({ text: text || '', bold: opts.bold || opts.header })],
      }),
    ],
  })
}

/**
 * Renders a `createdAt` epoch + duration as `<date> <time> ～ <endTime>`,
 * composing {@link formatDate} and {@link formatTime} separately (rather than
 * one combined `Intl.DateTimeFormat` call) so the date always precedes the
 * time. A combined date+time format lets some locales (e.g. `vi`) order
 * their own pattern time-first, which looked inconsistent with the web
 * app's always-date-first display once the export locale was Vietnamese
 * (BRAI-226).
 */
function formatMeetingDateTimeRange(
  createdAt: number | undefined,
  durationSeconds: number | undefined,
  locale: MeetingMinutesLocale
): string {
  if (!createdAt) return ''
  const base = `${formatDate(createdAt, locale)} ${formatTime(createdAt, locale)}`
  if (!durationSeconds) return base
  const end = createdAt + durationSeconds * 1000
  return `${base} ～ ${formatTime(end, locale)}`
}

function buildMeetingInfoTable(s: MinutesStrings, options: BuildOptions): Table {
  const dateTime =
    formatMeetingDateTimeRange(options.createdAt, options.durationSeconds, options.locale) ||
    s.empty
  const participants = options.participants?.length ? options.participants.join(', ') : s.empty

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    // Body cells below repeat the same per-column width as the header so Word
    // keeps the 25/75 split instead of auto-fitting columns to content — wide
    // CJK text otherwise pushes columns out of proportion (BRAI-207).
    layout: TableLayoutType.FIXED,
    borders: TABLE_BORDERS,
    rows: [
      new TableRow({
        tableHeader: true,
        children: [
          cell(s.itemColumn, { header: true, width: 25 }),
          cell(s.contentColumn, { header: true, width: 75 }),
        ],
      }),
      new TableRow({
        children: [
          cell(s.meetingName, { bold: true, width: 25 }),
          cell(options.title || s.empty, { width: 75 }),
        ],
      }),
      new TableRow({
        children: [cell(s.dateTime, { bold: true, width: 25 }), cell(dateTime, { width: 75 })],
      }),
      new TableRow({
        children: [
          cell(s.participants, { bold: true, width: 25 }),
          cell(participants, { width: 75 }),
        ],
      }),
    ],
  })
}

/**
 * Build section 3 (議論内容 / Nội dung thảo luận) by rendering each topic as
 * "議題N：{title}" then sub-blocks 概要 / 主な議論 / 結論. Missing sub-fields
 * fall back to the localized "—" placeholder so the template structure is
 * always preserved.
 */
function buildDiscussionSections(s: MinutesStrings, minutes: MeetingMinutesData): Paragraph[] {
  const blocks: Paragraph[] = []
  if (!minutes.topics.length) {
    blocks.push(plain(s.empty))
    return blocks
  }

  minutes.topics.forEach((topic, idx) => {
    blocks.push(
      new Paragraph({
        spacing: { before: 200, after: 80 },
        children: [
          new TextRun({ text: `${s.topic(idx + 1)}：${topic.title || s.empty}`, bold: true }),
        ],
      })
    )

    blocks.push(subHeading(s.overview))
    blocks.push(plain(topic.overview || s.empty))

    blocks.push(subHeading(s.mainDiscussion))
    if (topic.discussions.length) {
      topic.discussions.forEach((item) => blocks.push(bullet(item)))
    } else {
      blocks.push(plain(s.empty))
    }

    blocks.push(subHeading(s.conclusion))
    if (topic.conclusions.length) {
      topic.conclusions.forEach((item) => blocks.push(bullet(item)))
    } else {
      blocks.push(plain(s.empty))
    }
  })

  return blocks
}

function buildActionItemsTable(s: MinutesStrings, minutes: MeetingMinutesData): Table {
  const items = minutes.action_items
  const rows: TableRow[] = [
    new TableRow({
      tableHeader: true,
      children: [
        cell(s.no, { header: true, width: 10 }),
        cell(s.taskContent, { header: true, width: 50 }),
        cell(s.assignee, { header: true, width: 20 }),
        cell(s.deadline, { header: true, width: 20 }),
      ],
    }),
  ]

  if (items.length) {
    items.forEach((it, i) =>
      rows.push(
        new TableRow({
          children: [
            cell(`A-${String(i + 1).padStart(2, '0')}`, { width: 10 }),
            cell(it.content || s.empty, { width: 50 }),
            cell(it.assignee || s.empty, { width: 20 }),
            cell(it.deadline || s.empty, { width: 20 }),
          ],
        })
      )
    )
  } else {
    rows.push(
      new TableRow({
        children: [
          cell(s.empty, { width: 10 }),
          cell(s.empty, { width: 50 }),
          cell(s.empty, { width: 20 }),
          cell(s.empty, { width: 20 }),
        ],
      })
    )
  }

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    // Body cells above repeat the same per-column width as the header so Word
    // keeps the 10/10/40/20/20 split instead of auto-fitting columns to content —
    // long Japanese task descriptions otherwise push No/Status/担当/期限 out of
    // proportion (BRAI-207).
    layout: TableLayoutType.FIXED,
    borders: TABLE_BORDERS,
    rows,
  })
}

/**
 * Build a Word document from meeting minutes JSON, choosing labels per locale
 * to match the JP/VI templates shipped with the product.
 *
 * @param minutes Normalized response from `/meeting/meeting-minutes`.
 * @param options Title/createdAt/duration/locale used to render headers.
 * @returns Docx `Document` ready to be packed into a Blob.
 */
export function buildMeetingMinutesDocument(
  minutes: MeetingMinutesData,
  options: BuildOptions
): Document {
  const s = STRINGS[options.locale]
  const children: (Paragraph | Table)[] = []

  children.push(
    new Paragraph({
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { after: 320 },
      children: [new TextRun({ text: s.documentTitle, bold: true, size: 36 })],
    })
  )

  children.push(sectionHeading(s.section1))
  children.push(buildMeetingInfoTable(s, options))

  children.push(sectionHeading(s.section2))
  if (minutes.purpose.length) {
    minutes.purpose.forEach((line) => children.push(bullet(line)))
  } else {
    children.push(plain(s.empty))
  }

  children.push(sectionHeading(s.section3))
  buildDiscussionSections(s, minutes).forEach((p) => children.push(p))

  children.push(sectionHeading(s.section4))
  children.push(buildActionItemsTable(s, minutes))

  return new Document({ sections: [{ children }] })
}

/**
 * Convenience wrapper that builds the document and packs it into a Blob ready
 * for `downloadBlob`.
 */
export async function meetingMinutesToDocxBlob(
  minutes: MeetingMinutesData,
  options: BuildOptions
): Promise<Blob> {
  const doc = buildMeetingMinutesDocument(minutes, options)
  return Packer.toBlob(doc)
}

/**
 * Trigger a browser download for an in-memory Blob.
 *
 * @param blob     Binary content to save.
 * @param filename Suggested filename including extension.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
